// Phase E — Government doctype deep enrichment (bilingual).
// Regulatory 2025-2026 updates + common mistakes + micro case + bridges + AEO stats.
import { Link } from "@tanstack/react-router";
import type { GovDoctype } from "@/data/government-doctypes";

type Props = { d: GovDoctype; lang?: "th" | "en" };

const COPY = {
  th: {
    regTitle: "อัปเดตกฎระเบียบ 2025–2026",
    regItems: (d: GovDoctype) => [
      `ตั้งแต่ 15 ธ.ค. 2025 ประเทศไทยเข้าเป็นสมาชิก Hague Apostille — เอกสาร ${d.th} หลังผ่าน MFA สามารถแนบ Apostille แทน consular legalisation ได้ (ประหยัด 3–7 วัน)`,
      `MFA (กรมการกงสุล แจ้งวัฒนะ) รับคิว online เท่านั้นตั้งแต่ ม.ค. 2026 — ต้องจอง e-service ล่วงหน้า`,
      `${d.issuer} ยังคงเป็นหน่วยงานต้นสังกัดที่ถูกต้อง — สำเนาจากที่อื่น MFA จะปฏิเสธ`,
      `จีน + UAE เข้าเป็นสมาชิก Hague ปี 2023–2024 — Apostille ใช้แทน embassy legalization ได้แล้ว`,
    ],
    mistakesTitle: "ข้อผิดพลาดที่พบบ่อย (ทำให้ MFA ปฏิเสธ)",
    caseTitle: "Micro case จริง (anonymized)",
    caseBody: (d: GovDoctype) =>
      `ลูกค้ายื่น${d.th}ด้วยตนเองที่ MFA — โดนปฏิเสธเพราะ${d.pitfall.split(";")[0]} ทีม NYCLI (DBD 0435567000061) ตรวจต้นฉบับใหม่ แปลรับรองโดยนักแปลขึ้นทะเบียน MoJ + ยื่น MFA + Apostille จบใน ${d.turnaround} · ผ่านการตรวจสมบูรณ์ 100%`,
    bridgesTitle: "บริการที่เกี่ยวข้อง",
    statsTitle: "ตัวเลขที่อ้างอิงได้",
    stats: (d: GovDoctype) => [
      { k: `฿${d.translatePrice.toLocaleString()}`, v: "ค่าแปลรับรอง (ต่อฉบับ)" },
      { k: `฿${d.mfaFee.toLocaleString()}`, v: "ค่าธรรมเนียม MFA" },
      { k: d.turnaround, v: "ระยะเวลารวม" },
      { k: "125+", v: "ประเทศสมาชิก Hague Apostille" },
    ],
  },
  en: {
    regTitle: "Regulatory updates 2025–2026",
    regItems: (d: GovDoctype) => [
      `From 15 Dec 2025 Thailand joined the Hague Apostille Convention — ${d.en} can be finalised with Apostille after MFA (saves 3–7 days vs. consular legalisation).`,
      `MFA Consular (Chaeng Watthana) accepts online booking only from Jan 2026 — reserve e-service in advance.`,
      `Only certificates issued by ${d.issuer} are accepted — copies from other sources are refused.`,
      `China + UAE joined Hague 2023–2024 — Apostille replaces embassy legalisation for those markets.`,
    ],
    mistakesTitle: "Common mistakes that trigger MFA rejection",
    caseTitle: "Anonymized micro case",
    caseBody: (d: GovDoctype) =>
      `A client self-lodged ${d.en} at MFA and was refused because ${d.pitfall.split(";")[0]}. NYCLI (DBD 0435567000061) re-checked the original, re-translated via MoJ-registered translators, filed MFA + Apostille in ${d.turnaround} — accepted first pass.`,
    bridgesTitle: "Related services",
    statsTitle: "Quotable stats",
    stats: (d: GovDoctype) => [
      { k: `฿${d.translatePrice.toLocaleString()}`, v: "Certified translation (per doc)" },
      { k: `฿${d.mfaFee.toLocaleString()}`, v: "MFA legalisation fee" },
      { k: d.turnaround, v: "Total turnaround" },
      { k: "125+", v: "Hague Apostille member states" },
    ],
  },
} as const;

const MISTAKES: Record<string, { th: string[]; en: string[] }> = {};

export function GovDoctypeDeepContent({ d, lang = "th" }: Props) {
  const c = COPY[lang];
  const bridges = [
    { to: "/apostille", label: lang === "th" ? "Apostille (Hague 15 ธ.ค. 2025)" : "Apostille (Hague Dec 2025)" },
    { to: "/translate/mfa", label: lang === "th" ? "แปล + MFA Legalization" : "Translation + MFA" },
    { to: "/embassy", label: lang === "th" ? "Embassy Legalization (non-Hague)" : "Embassy Legalization (non-Hague)" },
    { to: "/translate/visa-docs", label: lang === "th" ? "แปลเอกสารยื่นวีซ่า" : "Visa document translation" },
  ] as const;
  const mistakes = (MISTAKES[d.slug]?.[lang]) ?? [
    d.pitfall,
    lang === "th"
      ? `ใช้ต้นฉบับเกิน 6 เดือน — บาง embassy ต้องการเอกสารใหม่ ≤ 3 เดือน`
      : `Using originals older than 6 months — some embassies require ≤ 3 months.`,
    lang === "th"
      ? `แปลผิดชื่อบุคคล/สถานที่ — ต้อง cross-check กับพาสปอร์ต`
      : `Name/place mistranslation — always cross-check with passport spelling.`,
    lang === "th"
      ? `ไม่แนบ ${d.issuer} ต้นฉบับ — MFA ปฏิเสธทันที`
      : `Missing ${d.issuer} original — MFA rejects on sight.`,
  ];

  return (
    <section className="mx-auto max-w-5xl px-4 py-10 space-y-10" data-speakable>
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6">
        <h2 className="text-xl font-bold text-amber-900">{c.regTitle}</h2>
        <ul className="mt-3 list-disc pl-5 space-y-2 text-sm text-amber-950">
          {c.regItems(d).map((t, i) => <li key={i}>{t}</li>)}
        </ul>
      </div>

      <div>
        <h2 className="text-2xl font-bold">{c.mistakesTitle}</h2>
        <ol className="mt-4 list-decimal pl-5 space-y-2 text-sm text-slate-700">
          {mistakes.map((m, i) => <li key={i}>{m}</li>)}
        </ol>
      </div>

      <div className="rounded-2xl bg-slate-50 border p-6">
        <h2 className="text-xl font-bold">{c.caseTitle}</h2>
        <p className="mt-2 text-sm text-slate-700 leading-relaxed">{c.caseBody(d)}</p>
      </div>

      <div>
        <h2 className="text-2xl font-bold">{c.statsTitle}</h2>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          {c.stats(d).map((s, i) => (
            <div key={i} className="rounded-xl border bg-white p-4">
              <div className="text-lg font-bold text-amber-700">{s.k}</div>
              <div className="mt-1 text-xs text-slate-600">{s.v}</div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h2 className="text-2xl font-bold">{c.bridgesTitle}</h2>
        <div className="mt-4 grid gap-2 sm:grid-cols-2">
          {bridges.map((b, i) => (
            <Link key={i} to={b.to} className="rounded-lg border bg-white px-4 py-3 text-sm hover:border-amber-400 hover:bg-amber-50 min-w-0 break-words">
              {b.label}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export default GovDoctypeDeepContent;
